import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";


import InputForms from "components/UiComponents/forms/InputForms";
import { FaUserAlt } from "react-icons/fa";
import { AiOutlineMail } from "react-icons/ai";
import { FiCopy, FiAlertTriangle } from "react-icons/fi";
import { MdRefresh } from "react-icons/md";
import styles from "./StepAccount.module.scss";
import InputPassword from "components/UiComponents/forms/InputPassword";
import Axios from "_services/caller.service";

const StepAccount = ({ userData, updateUserData, triggerValidation, onConflictChange, onValidChange }) => {
  const { t } = useTranslation();
  const [validation, setValidation] = useState("");
  const [validationType, setValidationType] = useState(""); // 'error' ou 'success'
  const [touched, setTouched] = useState({});
  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));
  const [existingUser, setExistingUser] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [loginConflict, setLoginConflict] = useState(false);
  const [emailConflict, setEmailConflict] = useState(false);
  const [password2, setPassword2] = useState("");
  const emailCheckTimer = useRef(null);
  const loginCheckTimer = useRef(null);

  const [groups, setGroups] = useState([]);
  const [supportLevels, setSupportLevels] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingSupport, setLoadingSupport] = useState(false);

  // Notify parent of overall step validity
  useEffect(() => {
    if (!onValidChange) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasLogin = (userData.login || "").trim().length > 0;
    const hasEmail = (userData.email || "").trim().length > 0 && emailRegex.test(userData.email);
    const hasName = (userData.name || "").trim().length > 0;
    const hasLastName = (userData.lastName || "").trim().length > 0;
    const hasPassword = (userData.password || "").length > 0;
    const hasPwd2 = password2.length > 0;
    const passwordsMatch = hasPwd2 && userData.password === password2;
    const noConflicts = !emailConflict && !loginConflict;
    onValidChange(hasLogin && hasEmail && hasName && hasLastName && hasPassword && passwordsMatch && noConflicts);
  }, [userData.login, userData.email, userData.name, userData.lastName, userData.password, password2, emailConflict, loginConflict, onValidChange]);

  const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1);

  // Validation
  const validateStep = useCallback(() => {
    if (!userData.login || !userData.email || !userData.password) {
      let missingFields = [];
      if (!userData.login) missingFields.push("Login");
      if (!userData.email) missingFields.push(t("common.email"));
      if (!userData.password) missingFields.push(t("common.password"));

      setValidation(
        t("validation.fields_required", { fields: missingFields.join(", ") })
      );
      setValidationType("error");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      setValidation(t("validation.invalid_email_format"));
      setValidationType("error");
      return false;
    }

    if (emailConflict || loginConflict) {
      const conflicts = [];
      if (loginConflict) conflicts.push("login");
      if (emailConflict) conflicts.push("email");
      setValidation(t("validation.login_conflict", { conflicts: conflicts.join(" et l'") }));
      setValidationType("error");
      return false;
    }

    if (password2 && userData.password !== password2) {
      setValidation(t("common.passwords_not_match"));
      setValidationType("error");
      return false;
    }

    setValidation(t("validation.account_validated"));
    setValidationType("success");

    return true;
  }, [userData.login, userData.email, userData.password, emailConflict, loginConflict, password2]);

  // Validation déclenchée depuis le parent
  useEffect(() => {
    if (triggerValidation > 0) {
      validateStep();
    }
  }, [triggerValidation, validateStep]);

  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()";
    const passwordLength = 12;
    let generatedPassword = "";
    for (let i = 0; i < passwordLength; i++) {
      generatedPassword += chars.charAt(
        Math.floor(Math.random() * chars.length)
      );
    }

    updateUserData({ password: generatedPassword });
    setPassword2(generatedPassword);
    setValidation("");
    setValidationType("");
  };

  // Charger les groupes depuis le backend
  useEffect(() => {
    setLoadingGroups(true);
    Axios.get("/permissions-levels-groups")
      .then((res) => {
        setGroups(res.data);
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des groupes:", err);
        setGroups([]);
      })
      .finally(() => setLoadingGroups(false));
  }, []);

  // Charger les niveaux de support depuis le backend
  useEffect(() => {
    if (userData.role === "supportTeam") {
      setLoadingSupport(true);
      Axios.get("/permissions-levels-support")
        .then((res) => {
          console.log("✅ Niveaux de support reçus:", res.data);
          setSupportLevels(res.data);
        })
        .catch((err) => {
          console.error("❌ Erreur lors du chargement des niveaux de support:", err);
          setSupportLevels([]);
        })
        .finally(() => setLoadingSupport(false));
    }
  }, [userData.role]);

  // Reset group/supportLevel quand on change de rôle
  const handleRoleChange = (e) => {
    const selectedRole = e.target.value;
    updateUserData({
      role: selectedRole,
      group: "",
      groupName: "",
      supportLevel: "",
      supportLevelName: "",
    });
  };

  // Sélection groupe
  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    const groupObj = groups.find((g) => String(g.id) === String(groupId));
    updateUserData({
      group: groupId,
      groupName: groupObj ? groupObj.name : "",
    });
  };

  // Sélection support level
  const handleSupportChange = (e) => {
    const supportId = e.target.value;
    const supportObj = supportLevels.find((s) => String(s.id) === String(supportId));
    updateUserData({
      supportLevel: supportId,
      supportLevelName: supportObj ? supportObj.name : "",
    });
  };

  // Notify parent of conflict changes
  useEffect(() => {
    if (onConflictChange) {
      onConflictChange(emailConflict || loginConflict);
    }
  }, [emailConflict, loginConflict, onConflictChange]);

  // Vérification email existant avec debounce
  const checkEmailExists = useCallback((email) => {
    if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setExistingUser(null);
      setEmailConflict(false);
      return;
    }

    emailCheckTimer.current = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const res = await Axios.post("/check-email-exists", { email: email.trim() });
        if (res.data?.emailExists) {
          setExistingUser(res.data.emailUser);
          setEmailConflict(true);
        } else {
          setExistingUser(null);
          setEmailConflict(false);
        }
      } catch (err) {
        console.error("Erreur vérification email:", err);
        setExistingUser(null);
        setEmailConflict(false);
      } finally {
        setCheckingEmail(false);
      }
    }, 500);
  }, []);

  // Vérification login existant avec debounce
  const checkLoginExists = useCallback((login) => {
    if (loginCheckTimer.current) clearTimeout(loginCheckTimer.current);

    if (!login || login.trim().length < 2) {
      setLoginConflict(false);
      return;
    }

    loginCheckTimer.current = setTimeout(async () => {
      try {
        const res = await Axios.post("/check-email-exists", { login: login.trim() });
        if (res.data?.loginExists) {
          setLoginConflict(true);
        } else {
          setLoginConflict(false);
        }
      } catch (err) {
        console.error("Erreur vérification login:", err);
        setLoginConflict(false);
      }
    }, 500);
  }, []);

  const handleEmailChange = (e) => {
    const email = e.target.value;
    updateUserData({ email });
    checkEmailExists(email);
  };

  const handleLoginChange = (e) => {
    const login = e.target.value;
    updateUserData({ login });
    checkLoginExists(login);
  };

  const handleCopyPassword = () => {
    if (userData.password) {
      navigator.clipboard.writeText(userData.password);
      setValidation(t("validation.password_copied"));
      setValidationType("success");
      setTimeout(() => {
        setValidation("");
        setValidationType("");
      }, 2000);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateStep();
  };

  return (
    <form onSubmit={handleSubmit} className={styles.formContainer}>
      {validation && (
        <p className={`${styles.validationMessage} ${styles[validationType]}`}>
          {validation}
        </p>
      )}

      {/* Login & Email en grid 2 colonnes */}
      <div className={styles.grid2}>
        <div className={styles.fieldGroup} onBlur={() => markTouched("login")}>
          <label htmlFor="login">Login</label>
          <InputForms
            id="login"
            type="text"
            placeholder="Login"
            value={userData.login}
            onChange={handleLoginChange}
            required
            svg={<FaUserAlt />}
            paddingLeft="40px"
          />
          {loginConflict && (
            <span className={styles.fieldError}>{t("validation.login_already_used")}</span>
          )}
          {touched.login && !userData.login?.trim() && !loginConflict && (
            <span className={styles.fieldError}>{t("validation.login_required")}</span>
          )}
        </div>

        <div className={styles.fieldGroup} onBlur={() => markTouched("email")}>
          <label htmlFor="email">{t("common.email")}</label>
          <InputForms
            id="email"
            type="email"
            placeholder={t("common.email")}
            value={userData.email}
            onChange={handleEmailChange}
            required
            svg={<AiOutlineMail />}
            paddingLeft="40px"
          />
          {emailConflict && !existingUser && (
            <span className={styles.fieldError}>{t("validation.email_already_in_use")}</span>
          )}
          {touched.email && !userData.email?.trim() && !emailConflict && (
            <span className={styles.fieldError}>{t("validation.email_required")}</span>
          )}
          {userData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email) && (
            <span className={styles.fieldError}>{t("common.format_email_invalid")}</span>
          )}
        </div>
      </div>

      {existingUser && (
        <div className={styles.existingUserBanner}>
          <div className={styles.bannerIcon}>
            <FiAlertTriangle />
          </div>
          <div className={styles.bannerContent}>
            <strong>{t("common.account_exists_email")}</strong>
            <span>
              {existingUser.display_name} (@{existingUser.login}) — {existingUser.email}
            </span>
          </div>
        </div>
      )}

      {/* Nom & Prénom en grid 2 colonnes */}
      <div className={styles.grid2}>
        <div className={styles.fieldGroup} onBlur={() => markTouched("lastName")}>
          <label htmlFor="lastName">{t("common.lastname")}</label>
          <InputForms
            id="lastName"
            type="text"
            placeholder={t("common.lastname")}
            value={userData.lastName}
            onChange={(e) =>
              updateUserData({ lastName: capitalize(e.target.value) })
            }
            required
            svg={<FaUserAlt />}
            paddingLeft="40px"
          />
          {touched.lastName && !userData.lastName?.trim() && (
            <span className={styles.fieldError}>{t("validation.name_required")}</span>
          )}
        </div>

        <div className={styles.fieldGroup} onBlur={() => markTouched("name")}>
          <label htmlFor="name">{t("common.firstname")}</label>
          <InputForms
            id="name"
            type="text"
            placeholder={t("common.firstname")}
            value={userData.name}
            onChange={(e) =>
              updateUserData({ name: capitalize(e.target.value) })
            }
            required
            svg={<FaUserAlt />}
            paddingLeft="40px"
          />
          {touched.name && !userData.name?.trim() && (
            <span className={styles.fieldError}>{t("validation.firstname_required")}</span>
          )}
        </div>
      </div>

      {/* Groupe (dynamique) */}
      <div className={styles.fieldGroup}>
        <label htmlFor="group">{t("validation.group_label")}</label>
        <select
          id="group"
          value={userData.group || ""}
          onChange={handleGroupChange}
          className={styles.selectInput}
        >
          <option value="">{t("common.select_group")}</option>
          {loadingGroups ? (
            <option disabled>{t("common.loading")}</option>
          ) : groups.length > 0 ? (
            groups.map((grp) => (
              <option key={grp.id} value={grp.id}>
                {grp.name}
              </option>
            ))
          ) : (
            <option disabled>{t("validation.no_group_available")}</option>
          )}
        </select>
      </div>

      {/* Password */}
      <div className={styles.passwordSection}>
        <label htmlFor="password">{t("common.password")}</label>
        <div className={styles.passwordInputGroup} onBlur={() => markTouched("password")}>
          <InputPassword
            id="password"
            placeholder={
              userData.password !== "" ? userData.password : t("common.password")
            }
            value={userData.password}
            onChange={(e) => updateUserData({ password: e.target.value })}
            required
            paddingLeft="40px"
          />
          {touched.password && !userData.password && (
            <span className={styles.fieldError}>{t("validation.password_required")}</span>
          )}
        </div>

        <label htmlFor="password2">{t("common.password_confirm")}</label>
        <div className={styles.passwordInputGroup}>
          <InputPassword
            id="password2"
            placeholder={t("common.password_confirm")}
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            required
            paddingLeft="40px"
          />
        </div>
        {password2 && userData.password !== password2 && (
          <span className={styles.fieldError}>{t("common.passwords_not_match")}</span>
        )}

        <div className={styles.passwordButtonsRow}>
          <button
            type="button"
            onClick={generatePassword}
            className={styles.btnPrimary}
          >
            <MdRefresh /> {t("common.generate")}
          </button>
          {userData.password && (
            <button
              type="button"
              onClick={handleCopyPassword}
              className={styles.copyBtn}
              title={t("common.copy_password")}
            >
              <FiCopy size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Support Level (dynamique) */}
      {userData.role === "supportTeam" && (
        <div className={styles.fieldGroup}>
          <label htmlFor="supportLevel">{t("validation.support_level_label")}</label>
          <select
            id="supportLevel"
            value={userData.supportLevel || ""}
            onChange={handleSupportChange}
            required
            className={styles.selectInput}
          >
            <option value="">{t("common.select_level")}</option>
            {loadingSupport ? (
              <option disabled>{t("common.loading")}</option>
            ) : supportLevels.length > 0 ? (
              supportLevels.map((lvl) => (
                <option key={lvl.id} value={lvl.id}>
                  {lvl.name}
                </option>
              ))
            ) : (
              <option disabled>{t("validation.no_level_available")}</option>
            )}
          </select>
        </div>
      )}
    </form>
  );
};

export default StepAccount;
